import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

const Footer = () => {
  const { t } = useTranslation();

  const currentYear = new Date().getFullYear();

  const menuItems = [
    { text: t('footer.links.home'), path: '/' },
    { text: t('footer.links.fleet'), path: '/fleet' },
    // { text: t('footer.links.services'), path: '/services' },
    { text: t('footer.links.reservation'), path: '/reservation' },
    { text: t('footer.links.events'), path: '/events' },
    { text: t('footer.links.corporate'), path: '/corporate' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'black',
        color: 'white',
        pt: 6,
        pb: 3,
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo et Description */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="/images/logo_noir.jpg"
              alt="O.KSI Luxury Services"
              sx={{ width: "100%", mb: 2 }}
            />
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('footer.description')}
            </Typography>
          </Grid>

          {/* Navigation */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: theme => theme.palette.secondary.main }}>
              {t('footer.links.title')}
            </Typography>
            <Grid container spacing={2}>
              {menuItems.map((item) => (
                <Grid item xs={6} key={item.text}>
                  <Link
                    component={Link}
                    to={item.path}
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      '&:hover': {
                        color: theme => theme.palette.secondary.main,
                      }
                    }}
                  >
                    {item.text}
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: theme => theme.palette.secondary.main }}>
              {t('footer.contact.title')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOnIcon sx={{ mr: 1, color: theme => theme.palette.secondary.main }} />
                <Box
                  component="a"
                  href="https://www.google.com/maps/search/?api=1&query=75+Rue+Rouget+de+Lisle+92150+Suresnes"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  {t('footer.contact.address')}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon sx={{ mr: 1, color: theme => theme.palette.secondary.main }} />
                <Box
                  component="a"
                  href="tel:+33663822888" 
                  sx={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  {t('footer.contact.phone')}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: theme => theme.palette.secondary.main }} />
                <Box
                  component="a"
                  href="mailto:o.ksiluxuryservices@outlook.com"
                  sx={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  {t('footer.contact.email')}
                </Box>
              </Box>
            </Box>

            {/* Réseaux sociaux */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: theme => theme.palette.secondary.main }}>
                {t('footer.social.title')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  href="https://facebook.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'white',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton 
                  href="https://instagram.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'white',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton 
                  href="https://linkedin.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: 'white',
                    '&:hover': {
                      color: theme => theme.palette.secondary.main,
                    }
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            mt: 4,
            pt: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
            {currentYear} O.KSI Luxury Services. {t('footer.legal.copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
